import { KeyValue } from "@/main/models/common";
import { DamagePartAIResponse } from "@/main/models/response";

import { DamageZoneEnum, IDamageAreasByZone, IDamageZone } from "./types";

export const CAR_VIEW_BOX = "0 0 240 420";

export const CAR_BODY_PATH =
	"M96 20H144C168 21 184 30 191 46C197 60 200 80 200 104V316C200 344 196 366 188 380C181 392 168 399 152 400H88C72 399 59 392 52 380C44 366 40 344 40 316V104C40 80 43 60 49 46C56 30 72 21 96 20Z";

export const CAR_CABIN_PATH = "M88 152L72 198V276L86 320H154L168 276V198L152 152Z";

export const DAMAGE_ZONES: IDamageZone[] = [
	{
		zone: DamageZoneEnum.Front,
		path: "M0 0H240V152H152L168 198H72L88 152H0Z",
		labelX: 120,
		labelY: 102,
		translateKey: "information.damageZoneFront",
		keywords: ["frent", "diant", "delanter", "front", "capo", "cofre"],
	},
	{
		zone: DamageZoneEnum.Left,
		path: "M0 152H88L72 198V276L86 320H0Z",
		labelX: 56,
		labelY: 237,
		labelRotate: -90,
		translateKey: "information.damageZoneLeft",
		keywords: ["esquerd", "izquierd", "left"],
	},
	{
		zone: DamageZoneEnum.Roof,
		path: "M72 198H168V276H72Z",
		labelX: 120,
		labelY: 242,
		translateKey: "information.damageZoneRoof",
		keywords: ["teto", "techo", "roof", "arriba", "cima", "superior"],
	},
	{
		zone: DamageZoneEnum.Right,
		path: "M240 152H152L168 198V276L154 320H240Z",
		labelX: 184,
		labelY: 237,
		labelRotate: -90,
		translateKey: "information.damageZoneRight",
		keywords: ["direit", "derech", "right"],
	},
	{
		zone: DamageZoneEnum.Rear,
		path: "M0 420H240V320H154L168 276H72L86 320H0Z",
		labelX: 120,
		labelY: 345,
		translateKey: "information.damageZoneRear",
		keywords: ["tras", "rear", "back", "cajuela", "porta-mala", "portamala"],
	},
];

export function normalizeLabel(value: string) {
	return String(value ?? "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase()
		.trim();
}

function matchZones(label: string) {
	return DAMAGE_ZONES.filter((item) => item.keywords.some((keyword) => label.includes(keyword)));
}

const GENDER_SUFFIX_REGEX = /(os|as|o|a)$/;
const AREA_TOKEN_MIN_LENGTH = 3;

function toAreaTokens(value: string) {
	return normalizeLabel(value)
		.replace(/\([^)]*\)/g, " ")
		.replace(/[^a-z0-9]+/g, " ")
		.split(" ")
		.filter((token) => token.length >= AREA_TOKEN_MIN_LENGTH)
		.map((token) => token.replace(GENDER_SUFFIX_REGEX, ""));
}

function scoreArea(area: KeyValue, tokens: string[], zones: DamageZoneEnum[]) {
	const scoreTokens = toAreaTokens(area.value).filter((token) => tokens.includes(token)).length;
	const scoreZones = matchZones(normalizeLabel(area.value)).filter((zone) => zones.includes(zone.zone)).length;

	return scoreTokens * 2 + scoreZones;
}

export function findDamageAreaByPosition(position: string, areasDomain: KeyValue[]) {
	const tokens = toAreaTokens(position);
	if (!tokens.length) return undefined;

	const zones = matchZones(normalizeLabel(position)).map((zone) => zone.zone);

	return areasDomain.reduce<{ area?: KeyValue; score: number }>(
		(best, area) => {
			const score = scoreArea(area, tokens, zones);
			return score > best.score ? { area, score } : best;
		},
		{ score: 0 },
	).area;
}

export function findDamageAreaOfPart(part: DamagePartAIResponse, areasDomain: KeyValue[]): KeyValue | undefined {
	const idDamageArea = part.idDamageArea ?? null;

	if (idDamageArea !== null) {
		const areaFound = areasDomain.find((area) => Number(area.key) === idDamageArea);
		if (areaFound) return areaFound;
		if (!areasDomain.length) return { key: idDamageArea, value: part.damageAreaName ?? "" };
	}

	return findDamageAreaByPosition(part.position, areasDomain);
}

export function extractDamageAreasFromAiParts(parts: DamagePartAIResponse[], areasDomain: KeyValue[] = []): KeyValue[] {
	const areasByKey = new Map<string, KeyValue>();

	parts.forEach((part) => {
		const areaFound = findDamageAreaOfPart(part, areasDomain);
		if (!areaFound || areasByKey.has(String(areaFound.key))) return;

		areasByKey.set(String(areaFound.key), areaFound);
	});

	return Array.from(areasByKey.values());
}

export function splitAreasByZone(areas: KeyValue[]): IDamageAreasByZone {
	const zones: Partial<Record<DamageZoneEnum, KeyValue>> = {};
	const chips: KeyValue[] = [];

	areas.forEach((area) => {
		const zonesMatched = matchZones(normalizeLabel(area.value));
		const zoneFound = zonesMatched.length === 1 ? zonesMatched[0] : undefined;

		if (zoneFound && !zones[zoneFound.zone]) {
			zones[zoneFound.zone] = area;
			return;
		}
		chips.push(area);
	});

	return { zones, chips };
}
